import Alert from '@liquidweb-dev/undercurrent/Alert';
import AlertTitle from '@liquidweb-dev/undercurrent/AlertTitle';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import toast from '@liquidweb-dev/undercurrent/toast';
import Button from '@liquidweb-dev/undercurrent/Button';
import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import useUsertaskUpdate from 'modules/queries/usertask/useUpdate';
import useUsertaskList from 'modules/queries/usertask/useList';
import { HookedServerSpecsCard } from 'components/SeverSpecsCard';

interface DialogScheduledCancellationProps
	extends Omit<DialogProps, 'onClose'> {
	uniqId: string;
	onClose: () => void;
	serverName?: string;
	nextRenewalDate?: string | null;
}

const DialogScheduledCancellation = ({
	uniqId,
	onClose,
	serverName,
	nextRenewalDate,
	...rest
}: DialogScheduledCancellationProps) => {
	const { mutate: updateUserTask, isLoading } = useUsertaskUpdate();

	const { data: userTasks } = useUsertaskList({
		task_type: 'subaccnt_termination',
		uniq_id: uniqId,
	});
	const uuid = userTasks?.items?.[0]?.code || '';
	const handleConfirmation = () => {
		updateUserTask(
			{ status: 'cancelled', code: uuid },
			{
				onSuccess: () => {
					toast.success('Cancellation removed', {
						description: `Service for ${serverName} renews on ${nextRenewalDate}`,
					});
					onClose();
				},
			},
		);
	};

	return (
		<Dialog onClose={onClose} {...rest}>
			<DialogTitle onClose={onClose}>Cancel service</DialogTitle>
			<DialogContent>
				<Stack sx={{ gap: 200 }}>
					<Typography variant="body-sm">
						Your cancellation will be removed and your service will continue.
					</Typography>

					<Alert severity="info" showIcon={false}>
						<AlertTitle>
							Next renewal date:{' '}
							<Typography sx={{ fontWeight: 'bold' }}>
								{nextRenewalDate}
							</Typography>
						</AlertTitle>
						<Typography variant="body-sm" sx={{ fontWeight: 400 }}>
							Your billing will continue as normal on this date.
						</Typography>
					</Alert>

					<HookedServerSpecsCard
						showCancellationSection
						uniqId={uniqId}
						cardHeaderTitle="Selected server"
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'flex-end' }}>
				<Button
					size="large"
					onClick={handleConfirmation}
					loading={isLoading}
					fullWidth
				>
					Keep my service
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DialogScheduledCancellation;
